import { useEffect, useState } from "react";
import { X, Crown, Shield, Flame, Calendar } from "@phosphor-icons/react";
import { api, extractApiError } from "../lib/api";
import AssessmentHistory from "./AssessmentHistory";

/**
 * Modal shown to squad owners / co-owners when they tap "View Profile" on a
 * roster row. Fetches /squads/{squad_id}/members/{user_id}/profile so the
 * leader can coach based on objective improvement data.
 */
export default function SquadMemberProfileModal({ squadId, userId, isOwner, isCoOwner, onClose }) {
  const [profile, setProfile] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    api.get(`/squads/${squadId}/members/${userId}/profile`)
      .then((r) => { if (alive) setProfile(r.data); })
      .catch((e) => { if (alive) setErr(extractApiError(e)); });
    return () => { alive = false; };
  }, [squadId, userId]);

  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-0 sm:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="squad-member-profile-modal"
    >
      <div
        className="bg-background border border-border max-w-2xl w-full my-8 sm:my-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-background border-b border-border px-5 py-3 flex items-center justify-between">
          <div className="label-mono text-volt">// OPERATOR PROFILE</div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            data-testid="squad-member-profile-close"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {err && (
            <div className="border border-destructive text-destructive p-3 text-sm" data-testid="squad-member-profile-error">
              {err}
            </div>
          )}

          {!profile && !err && (
            <div className="label-mono animate-pulse" data-testid="squad-member-profile-loading">LOADING...</div>
          )}

          {profile && (
            <>
              <div>
                <div className="font-display text-3xl font-black uppercase mb-1 flex items-center gap-2 flex-wrap" data-testid="squad-member-profile-name">
                  {profile.name || "Operator"}
                  {isOwner && (
                    <span className="inline-flex items-center gap-1 text-volt text-xs font-mono uppercase">
                      <Crown size={12} weight="fill" /> OWNER
                    </span>
                  )}
                  {isCoOwner && !isOwner && (
                    <span className="inline-flex items-center gap-1 text-volt text-xs font-mono uppercase border border-volt/60 px-1.5 py-0.5">
                      <Shield size={11} weight="fill" /> CO-OWNER
                    </span>
                  )}
                </div>
                <div className="text-sm text-muted-foreground" data-testid="squad-member-profile-email">{profile.email}</div>
                {profile.joined_at && (
                  <div className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1">
                    <Calendar size={11} weight="bold" />
                    Joined {new Date(profile.joined_at).toLocaleDateString()}
                  </div>
                )}
              </div>

              {/* Squad progress */}
              <div className="grid grid-cols-3 gap-3" data-testid="squad-member-profile-progress">
                <div className="border border-border p-3 text-center">
                  <div className="font-display text-2xl font-black">
                    {profile.squad_progress?.completions || 0}
                    <span className="text-muted-foreground text-sm font-normal">/{profile.squad_progress?.total_days || 0}</span>
                  </div>
                  <div className="label-mono">SESSIONS</div>
                </div>
                <div className="border border-border p-3 text-center">
                  <div className="inline-flex items-center gap-1 font-display text-2xl font-black">
                    <Flame size={18} weight="bold" className="text-volt" />
                    {profile.squad_progress?.current_streak || 0}
                  </div>
                  <div className="label-mono">STREAK</div>
                </div>
                <div className="border border-border p-3 text-center">
                  <div className="font-display text-2xl font-black">
                    {profile.squad_progress?.last_completed
                      ? new Date(profile.squad_progress.last_completed).toLocaleDateString()
                      : "—"}
                  </div>
                  <div className="label-mono">LAST LOG</div>
                </div>
              </div>

              {/* Current assessment snapshot */}
              <CurrentAssessmentCard assessment={profile.assessment} />

              {/* History */}
              <AssessmentHistory
                mode="member"
                history={profile.assessment_history}
                title="// IMPROVEMENT OVER TIME"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const CURRENT_FIELDS = [
  { key: "squat_1rm_lb", label: "Squat 1RM", unit: "lb" },
  { key: "bench_1rm_lb", label: "Bench 1RM", unit: "lb" },
  { key: "deadlift_1rm_lb", label: "Deadlift 1RM", unit: "lb" },
  { key: "ohp_1rm_lb", label: "OHP 1RM", unit: "lb" },
  { key: "max_pushups", label: "Push-ups", unit: "" },
  { key: "max_pullups", label: "Pull-ups", unit: "" },
  { key: "plank_seconds", label: "Plank", unit: "s" },
  { key: "body_weight_lb", label: "Body Weight", unit: "lb" },
];

function fmtSeconds(s) {
  if (s == null) return "—";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.round(s % 60);
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
    : `${m}:${String(sec).padStart(2, "0")}`;
}

function CurrentAssessmentCard({ assessment }) {
  if (!assessment || Object.values(assessment).every((v) => v == null)) {
    return (
      <div className="border border-dashed border-border p-4 text-sm text-muted-foreground" data-testid="squad-member-no-assessment">
        This operator hasn&apos;t completed an assessment yet.
      </div>
    );
  }
  return (
    <div data-testid="squad-member-current-assessment">
      <div className="label-mono text-volt mb-2">// CURRENT ASSESSMENT</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {CURRENT_FIELDS.map((f) => {
          const v = assessment[f.key];
          return (
            <div key={f.key} className="border border-border bg-card p-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{f.label}</div>
              <div className="font-display text-lg font-black">
                {v == null ? "—" : `${v}${f.unit ? " " + f.unit : ""}`}
              </div>
            </div>
          );
        })}
        {assessment.two_mile_run_seconds != null && (
          <div className="border border-border bg-card p-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">2-Mile Run</div>
            <div className="font-display text-lg font-black">{fmtSeconds(assessment.two_mile_run_seconds)}</div>
          </div>
        )}
        {assessment.twelve_mile_ruck_seconds != null && (
          <div className="border border-border bg-card p-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">12-Mile Ruck</div>
            <div className="font-display text-lg font-black">{fmtSeconds(assessment.twelve_mile_ruck_seconds)}</div>
          </div>
        )}
      </div>
      {assessment.notes && (
        <div className="mt-3 border border-border bg-card p-2 text-sm">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Notes</div>
          {assessment.notes}
        </div>
      )}
    </div>
  );
}
